import { Directive, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { AppointmentsTableComponent } from '../components/appointments-table/appointments-table.component';
import { AppointmentsCalendarComponent } from '../components/appointments-calendar/appointments-calendar.component';
import { FilterOptions } from '../components/appointments-filter-modal/appointments-filter-modal.component';
import { ProfileService } from '../services/profile.service';
import { PackageService } from '../services/package.service';
import { AppointmentService } from '../services/appointment.service';
import { LocationService } from '../services/location.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { getUserTimezone } from '../shared/utils/date.util';

/**
 * Shared base for AdminAppointmentsComponent and StaffAppointmentsComponent.
 * Contains all common state, lifecycle hooks, and event handlers.
 */
@Directive()
export abstract class BaseAppointmentsComponent implements OnInit, OnDestroy {
  // Appointment stats
  todaysAppointments = 0;
  completedAppointments = 0;
  pendingAppointments = 0;

  // Toolbar state
  currentView: 'list' | 'calendar' = 'list';
  currentLocation = '';
  currentLocationLabels: string[] = [];
  searchTerm = '';
  activeFilters: FilterOptions | null = null;

  // Search debouncing
  protected searchSubject = new Subject<string>();

  // Navigation state
  navLinks: { label: string; value: 'today' | 'upcoming' | 'past' }[] = [
    { label: 'Today', value: 'today' },
    { label: 'Upcoming', value: 'upcoming' },
    { label: 'Past', value: 'past' },
  ];
  activeNav: 'today' | 'upcoming' | 'past' = 'today';

  /** Drives the admin-only affordances in the shared page template (Add button, schedule modal). */
  abstract readonly isAdminUser: boolean;
  showScheduleAppointmentModal = false;
  @ViewChild(AppointmentsTableComponent) appointmentsTable?: AppointmentsTableComponent;
  @ViewChild(AppointmentsCalendarComponent) appointmentsCalendar?: AppointmentsCalendarComponent;

  // Subscription management
  protected locationsSubscription: Subscription = new Subscription();
  protected availableLocations: { id: string; branch: string }[] = [];
  protected readonly locationService = inject(LocationService);

  constructor(
    protected profileService: ProfileService,
    protected packageService: PackageService,
    protected appointmentService: AppointmentService
  ) {}

  /**
   * Initialize component: load locations and packages, subscribe to location updates and search term changes, select a default location if none, and load appointment statistics.
   * @example
   * gOnInit()
   * undefined
   * @returns {{void}} No return value.
   */
  ngOnInit(): void {
    this.profileService.loadLocations();
    this.packageService.loadPackages();

    // No default location: an empty selection means every location the shop has. Defaulting to
    // locations[0] scoped the list to the first branch while the count cards above it kept
    // totalling all of them, so a multi-location shop saw two different answers on one screen.
    this.locationsSubscription = this.profileService.locations$.subscribe(locations => {
      this.availableLocations = locations;
    });

    this.loadAppointmentStats();

    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(searchTerm => {
      this.searchTerm = searchTerm;
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.locationsSubscription.unsubscribe();
  }

  // ─── Stats ────────────────────────────────────────────────────────────────

  /**
   * Load appointment statistics and update component counters; on error, fall back to loading individual stats.
   * @example
   * this.loadAppointmentStats()
   * undefined
   * @returns {{void}} No return value; updates component state or triggers fallback on error.
   **/
  protected loadAppointmentStats(): void {
    this.appointmentService.getAppointmentStats().subscribe({
      next: stats => {
        this.todaysAppointments = stats.data.todays_appointments || 0;
        this.completedAppointments = stats.data.completed || 0;
        this.pendingAppointments = stats.data.pending || 0;
      },
      error: () => {
        this.loadIndividualStats();
      },
    });
  }

  protected getUserTimezone(): string {
    return getUserTimezone();
  }

  /**
   * Loads and updates counts for today's, completed, and pending appointments by subscribing to appointmentService observables.
   * @example
   * this.loadIndividualStats()
   * undefined
   * @returns {void} Updates todaysAppointments, completedAppointments, and pendingAppointments properties; logs errors to the console on failure.
   **/
  protected loadIndividualStats(): void {
    this.appointmentService.getTodaysAppointments().subscribe({
      next: response => {
        this.todaysAppointments = response.length || 0;
      },
      error: error => {
        console.error("❌ Error loading today's appointments:", error);
      },
    });
    this.appointmentService.getCompletedAppointments().subscribe({
      next: response => {
        this.completedAppointments = response.length || 0;
      },
      error: error => {
        console.error('❌ Error loading completed appointments:', error);
      },
    });
    this.appointmentService.getPendingAppointments().subscribe({
      next: response => {
        this.pendingAppointments = response.length || 0;
      },
      error: error => {
        console.error('❌ Error loading pending appointments:', error);
      },
    });
  }

  // ─── Getters ─────────────────────────────────────────────────────────────

  get todaysAppointmentsCount(): string {
    return this.todaysAppointments.toString();
  }
  get completedAppointmentsCount(): string {
    return this.completedAppointments.toString();
  }
  get pendingAppointmentsCount(): string {
    return this.pendingAppointments.toString();
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  onSearchChanged(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  onViewChanged(view: 'list' | 'calendar'): void {
    this.currentView = view;
  }

  onLocationChanged(locationLabels: string[]): void {
    this.currentLocationLabels = locationLabels;
    this.currentLocation = this.getLocationIdFromLabels(locationLabels);
    this.syncShopLocationForFilter(this.currentLocation);
  }

  onFilterClicked(): void {}

  onFiltersApplied(filters: FilterOptions): void {
    this.activeFilters = filters;
  }

  onLocationSelectionFromModal(locationLabels: string[]): void {
    this.currentLocationLabels = locationLabels;
    this.currentLocation = this.getLocationIdFromLabels(locationLabels);
    this.syncShopLocationForFilter(this.currentLocation);
  }

  onNavSelect(value: 'today' | 'upcoming' | 'past'): void {
    this.activeNav = value;
  }

  openScheduleAppointmentModal(): void {
    this.showScheduleAppointmentModal = true;
  }

  closeScheduleAppointmentModal(): void {
    this.showScheduleAppointmentModal = false;
  }

  /** Refresh the cards and whichever view is on screen after an appointment is created. */
  onAppointmentCreated(): void {
    this.loadAppointmentStats();
    if (this.currentView === 'list') {
      this.appointmentsTable?.loadAppointments();
    } else {
      this.appointmentsCalendar?.loadAppointments();
    }
  }

  onAppointmentUpdated(): void {
    this.loadAppointmentStats();
    this.appointmentsCalendar?.loadAppointments();
  }

  trackByIndex(index: number): number {
    return index;
  }

  protected getLocationIdFromLabels(locationLabels: string[]): string {
    if (locationLabels.length === 0) return '';
    const [firstLabel] = locationLabels;
    const matchedLocation = this.availableLocations.find(loc => loc.branch === firstLabel);
    return matchedLocation ? matchedLocation.id : firstLabel;
  }

  private syncShopLocationForFilter(locationId: string): void {
    if (locationId) {
      this.locationService.setShopLocationById(locationId);
    } else {
      this.locationService.clearShopLocation();
    }
  }
}
